/*
 * Users page.
 *
 * Master user roster. Sortable table of USERS joined to USER_SCORES with
 * a column picker that persists to localStorage (`rbx-users-cols`).
 * Optional columns expose less-common metadata dimensions (city,
 * country, state, company, ad group, manager). Clicking a row opens
 * the user detail drawer; clicking the risk pill opens the breakdown
 * modal.
 *
 * Calculations:
 *   - clickRate = signals.find(id='fail-rate').component * 100 from
 *     USER_SCORES, so the table and the score model never disagree.
 *   - completion % = COURSE_USER_COMPLETIONS[email] / total assigned.
 *   - lastLogin and dateCreated render as dates via data.toDateStr, and
 *     are off by default in the column picker (BD-912).
 *
 * Postgres mapping:
 *   - canonical `users` joined to `user_risk_scores` (latest computed)
 *     and `course_user_completions_v` materialised view.
 *   - server-side pagination + sort once the tenancy exceeds ~2k users.
 *
 * localStorage keys: `rbx-users-cols` JSON array of visible column ids.
 *
 * Integration hook: `useUsersQuery({ sortBy, direction, limit, offset,
 * scope })` returning { rows, total } cache 1h.
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Popover, Checkbox, FormControlLabel } from '@mui/material';
import { motion } from 'motion/react';
import { useExcludeDisabled } from '../disabledFilter';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MainCard from '../../../components/cards/MainCard';
import { USERS, COURSE_SEND_LOG, DATA_EPOCH, toDateStr, REMINDER_TRACKING_NOTE } from '../data';
import useDashboardData, { logInPeriod } from '../hooks/useDashboardData';
import { USER_SCORES, BANDS } from '../riskScore';
import { ExportMenu, StatusCell } from './DataTable';
import RiskBreakdownModal from './RiskBreakdownModal';
import ActiveFilters from './ActiveFilters';
import { toServerScope, userScopeIsEmails } from '../serverScope';

// Combined Summary table, replaces the old Engaged Users widget per the
// May 2026 review. One large table with click rate, completion, risk score
// and email side-by-side, plus a tick-box column picker for the tenancy's
// metadata fields. Engaged/idle is preserved as a filter.

// Left-aligned "View" affordance for drillable rows: replaces whole-row
// click so the click target is explicit, matching the rest of the portal.
function RowViewButton({ onClick }) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            sx={{
                borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                fontSize: 11, fontWeight: 600, py: '2px', px: '10px', minWidth: 0, textTransform: 'none',
                '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'transparent' },
            }}
        >
            View
        </Button>
    );
}

// Lazy + epoch-tracked so the score map follows the live hydrated data.
let scoreCache = null;
let scoreEpoch = -1;
const SCORE_BY_EMAIL = {
    get(email) {
        if (scoreEpoch !== DATA_EPOCH) {
            scoreCache = new Map(USER_SCORES.map((u) => [u.email, u]));
            scoreEpoch = DATA_EPOCH;
        }
        return scoreCache.get(email);
    },
};

export const COLUMNS = [
    { id: 'name',        label: 'Name',          always: true,    sortable: true,  type: 'string', accessor: (u) => u.name },
    { id: 'email',       label: 'Email',         always: true,    sortable: true,  type: 'string', accessor: (u) => u.email },
    { id: 'dept',        label: 'Department',    defaultOn: true, sortable: true,  type: 'string', accessor: (u) => u.dept },
    { id: 'location',    label: 'Location',      defaultOn: true, sortable: true,  type: 'string', accessor: (u) => u.location },
    { id: 'manager',     label: 'Line manager',  sortable: true,  type: 'string',  accessor: (u) => u.manager },
    { id: 'country',     label: 'Country',       sortable: true,  type: 'string',  accessor: (u) => u.country },
    { id: 'city',        label: 'City',          sortable: true,  type: 'string',  accessor: (u) => u.city },
    { id: 'company',     label: 'Company',       sortable: true,  type: 'string',  accessor: (u) => u.company },
    { id: 'adGroup',     label: 'AD group',      sortable: true,  type: 'string',  accessor: (u) => u.adGroup },
    { id: 'simsSent',       label: 'Sims sent',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.simsSent ?? null, format: (v) => v == null ? '—' : v, align: 'right' },
    { id: 'clickRate',   label: 'Click rate',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => clickRate(u), format: (v) => v == null ? '—' : `${v}%`, align: 'right' },
    { id: 'secondStagePct', label: '2nd stage',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.secondStagePct ?? null, format: (v) => v == null ? '—' : `${v}%`, align: 'right' },
    { id: 'reportedPct',    label: 'Reported',     defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.reportedPct ?? null, format: (v) => v == null ? '—' : `${v}%`, align: 'right' },
    { id: 'coursesSent',    label: 'Courses sent', defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.coursesSent ?? null, format: (v) => v == null ? '—' : v, align: 'right' },
    { id: 'reminders',      label: 'Reminders',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.reminders ?? null, format: (v) => v == null ? '—' : v, align: 'right' },
    { id: 'completion',  label: 'Completion',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.comp, format: (v) => `${v}%`, align: 'right' },
    { id: 'avgScore',       label: 'Avg score',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => u.avgScore ?? null, format: (v) => v == null ? '—' : `${v}%`, align: 'right' },
    { id: 'riskScore',   label: 'Risk score',    defaultOn: true, sortable: true,  type: 'number', accessor: (u) => (u._server ? u._riskScore : SCORE_BY_EMAIL.get(u.email)?.finalScore ?? null), align: 'right' },
    // ponytail: client-fallback rows carry neither date and show the placeholder,
    // same as every other server metric on that path. Hydrate from data.USERS_RAW
    // if the fallback ever needs to render real dates.
    { id: 'lastLogin',   label: 'Last login',    sortable: true,  type: 'string',  accessor: (u) => u.lastLogin ?? null,   format: (v) => toDateStr(v) || '—' },
    { id: 'dateCreated', label: 'Date created',  sortable: true,  type: 'string',  accessor: (u) => u.dateCreated ?? null, format: (v) => toDateStr(v) || '—' },
    { id: 'status',      label: 'Status',        sortable: true,  type: 'string',  accessor: (u) => u.disabled ? 'Disabled' : 'Active' },
];

function clickRate(u) {
    if (u && u._server) return u._clickRate;
    const score = SCORE_BY_EMAIL.get(u.email);
    if (!score || score.insufficient) return null;
    const fr = score.signals.find((s) => s.id === 'fail-rate');
    return fr ? Math.round(fr.component * 100) : null;
}

function compareValues(a, b, type) {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (type === 'number') return a - b;
    return String(a).localeCompare(String(b));
}

function defaultCols() {
    return COLUMNS.filter((c) => c.always || c.defaultOn).map((c) => c.id);
}

function loadCols() {
    try {
        const stored = JSON.parse(localStorage.getItem('rbx-users-cols'));
        if (Array.isArray(stored) && stored.length > 0) return stored;
    } catch { /* fall through */ }
    return defaultCols();
}

function ColumnPicker({ active, setActive }) {
    const [anchor, setAnchor] = useState(null);
    const toggle = (id) => {
        const col = COLUMNS.find((c) => c.id === id);
        if (col?.always) return;
        const next = active.includes(id) ? active.filter((x) => x !== id) : [...active, id];
        setActive(next);
        localStorage.setItem('rbx-users-cols', JSON.stringify(next));
    };
    return (
        <>
            <Button
                size="small" variant="outlined"
                startIcon={<ViewColumnIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{ borderRadius: '4px', borderColor: 'divider', color: 'text.secondary', fontSize: 11, py: '3px', px: '10px', textTransform: 'none', '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' } }}
            >
                Columns ({active.length})
            </Button>
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 1, mt: 0.5, border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderRadius: '8px', minWidth: 200 } }}
            >
                <Box sx={{ px: 1, py: 0.5, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                    Columns
                </Box>
                {COLUMNS.map((c) => (
                    <FormControlLabel
                        key={c.id}
                        control={
                            <Checkbox
                                checked={active.includes(c.id)}
                                disabled={c.always}
                                onChange={() => toggle(c.id)}
                                size="small"
                                sx={{ p: 0.5, color: 'text.disabled', '&.Mui-checked': { color: 'secondary.main' } }}
                            />
                        }
                        label={<Typography sx={{ fontSize: 12, color: c.always ? 'text.disabled' : 'text.primary' }}>{c.label}</Typography>}
                        sx={{ display: 'flex', m: 0, px: 1, py: 0.25, borderRadius: '4px', '&:hover': { bgcolor: 'action.hover' } }}
                    />
                ))}
            </Popover>
        </>
    );
}

function ScoreCell({ value }) {
    if (value == null) return <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>, </Typography>;
    const band = BANDS.find((b) => value >= b.min && value <= b.max) || BANDS[BANDS.length - 1];
    return (
        <Box
            sx={{
                display: 'inline-flex', alignItems: 'center',
                px: '8px', py: '2px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 11, fontWeight: 800, letterSpacing: '-0.01em',
                bgcolor: band.bg, color: band.textColor,
                transition: 'transform 120ms',
                '&:hover': { transform: 'translateY(-1px)' },
            }}
        >
            {value}
        </Box>
    );
}

const PAGE_SIZES = [50, 100, 200];
const EXPORT_LIMIT = 20000; // RecordCap in DashboardRepository

export function mapRosterRow(r) {
    return {
        name: `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email,
        email: r.email, dept: r.dept, location: r.location, city: r.city, country: r.country,
        state: r.state, company: r.company, adGroup: r.adGroup, manager: r.manager,
        disabled: !r.enabled, lastLogin: r.lastLogin ?? null, dateCreated: r.dateCreated ?? null,
        comp: r.completionPct ?? 0,
        simsSent: r.simsSent ?? null, secondStagePct: r.secondStagePct ?? null, reportedPct: r.reportedPct ?? null,
        coursesSent: r.coursesSent ?? null, avgScore: r.avgScore ?? null, reminders: r.reminders ?? null,
        _server: true, _clickRate: r.clickRate ?? null, _riskScore: r.riskScore ?? null,
        // Raw server risk signals for the score breakdown modal - the client
        // model can't reproduce the EWMA'd server score, so the modal renders
        // these instead when the row came from the roster aggregation.
        _factors: { failRate: r.failRate, repeatRate: r.repeatRate, stageDepth: r.stageDepth, trainingDebt: r.trainingDebt, courseLag: r.courseLag, baseScore: r.baseScore, simsInWindow: r.simsInWindow ?? r.simsSent, coursesSent: r.coursesInWindow ?? r.coursesSent, finalScore: r.riskScore, band: r.band },
    };
}

// Server-side roster: sort/filters/scope are computed over EVERY user in SQL (the
// raw client roster caps at 20k rows and big tenancies exceed it, so the client table was
// silently incomplete). Null rows when the aggregate is unavailable.
function useRoster({ scope, limit, sortBy, sortDir, excludeDisabled, period, startDate, endDate }, { skip = false } = {}) {
    // a user scope holding display NAMES can't resolve server-side (server matches emails) - use the client path
    const emailsOnly = userScopeIsEmails(scope);
    // period/startDate/endDate -> filters.dateRange so the aggregation windows
    // the engagement columns (sims sent, click rate, 2nd stage, reported,
    // courses sent, completion, avg score) to the selected range. Risk score /
    // band stay the fixed 180-day snapshot server-side.
    const q = useDashboardData({
        metric: 'users', aggregation: 'user_roster', limit,
        sort: { field: sortBy, direction: sortDir },
        includeDisabled: !excludeDisabled,
        scope: toServerScope(scope),
        period, startDate, endDate,
    }, { skip: skip || !emailsOnly });
    const raw = (emailsOnly && Array.isArray(q.data?.roster)) ? q.data.roster : null;
    const serverPath = emailsOnly && !skip;
    return {
        rows: raw ? raw.map(mapRosterRow) : null,
        total: q.data?.meta?.totalRecords,
        loading: q.isFetching,
        pending: serverPath && !raw && !q.isError,
        failed: serverPath && !raw && q.isError,
        retry: q.refetch,
    };
}

export default function UsersPage({ go, drill, scope, removeScope, initialSortBy, initialSortDir, period, startDate, endDate }) {
    // Sort can be seeded by drill context (e.g. the Courses Users card lands
    // here ordered by completion ascending); fully editable after landing.
    const [sortBy, setSortBy] = useState(initialSortBy || 'riskScore');
    const [sortDir, setSortDir] = useState(initialSortDir || 'desc');
    const [active, setActive] = useState(loadCols);
    // BD-1016: driven by the dashboard-wide "Exclude disabled users" chip (off by default).
    const excludeDisabled = useExcludeDisabled();
    const [pageSize, setPageSize] = useState(50);
    const [modal, setModal] = useState(null);

    const roster = useRoster({ scope, limit: pageSize, sortBy, sortDir, excludeDisabled, period, startDate, endDate });
    const serverRows = roster.rows;
    const serverTotal = roster.total;

    // Export covers every user in scope, not just the loaded page.
    // ponytail: one 20k-row pull per click, stream server-side if tenancies outgrow it
    const [wantAll, setWantAll] = useState(false);
    const exportRoster = useRoster(
        { scope, limit: EXPORT_LIMIT, sortBy, sortDir, excludeDisabled, period, startDate, endDate },
        { skip: !wantAll || !serverRows },
    );

    const filtered = useMemo(() => {
        const s = scope || {};
        const isIn = (val, vs) => !vs || (Array.isArray(vs) ? vs.includes(val) : val === vs);
        // Completion over the selected period (BD-1003); USERS carries an all-time figure.
        const byEmail = new Map();
        for (const e of logInPeriod(COURSE_SEND_LOG, { period, startDate, endDate })) {
            const c = byEmail.get(e.email) || { sent: 0, done: 0 };
            c.sent += 1;
            if (e.dateCompleted) c.done += 1;
            byEmail.set(e.email, c);
        }
        const windowedComp = (u) => { const c = byEmail.get(u.email); return c ? Math.round((c.done / c.sent) * 100) : 0; };
        return USERS.filter((u) => {
            if (excludeDisabled && u.disabled) return false;
            if (!isIn(u.dept,     s.department)) return false;
            if (!isIn(u.location, s.location))   return false;
            if (!isIn(u.country,  s.country))    return false;
            if (s.user) {
                const arr = Array.isArray(s.user) ? s.user : [s.user];
                if (!arr.some((v) => u.email === v || u.name === v)) return false;
            }
            return true;
        }).map((u) => ({ ...u, comp: windowedComp(u) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, excludeDisabled, period, startDate, endDate, DATA_EPOCH]);

    const sorted = useMemo(() => {
        const col = COLUMNS.find((c) => c.id === sortBy);
        if (!col) return filtered;
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => compareValues(col.accessor(a), col.accessor(b), col.type) * dir);
    }, [filtered, sortBy, sortDir]);

    const serverBlocked = roster.pending || roster.failed;
    const rows = serverRows || (serverBlocked ? [] : sorted);
    const totalCount = serverRows ? (serverTotal ?? serverRows.length) : rows.length;
    const visible = rows.slice(0, pageSize);
    const activeCols = COLUMNS.filter((c) => active.includes(c.id));

    const modalProps = modal || {};

    const requestSort = (id) => {
        if (sortBy === id) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else { setSortBy(id); setSortDir(id === 'name' || id === 'email' ? 'asc' : 'desc'); }
    };


    return (
        <motion.div className="page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <div>
                    <div className="ptitle">Users</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>One combined table across sims, courses and risk - click a row for activity, click a score for breakdown</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{REMINDER_TRACKING_NOTE}</div>
                </div>
            </Box>

            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                {/* Toolbar. Search removed (the Filter-by-user picker covers finding a
                    user, matching the other pages); the engaged/idle toggle removed per
                    review - engagement is covered by the sims/courses columns now. The
                    exclude-disabled checkbox moved to the dashboard-wide chip (BD-1016). */}
                <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1 }} />
                    {!serverBlocked && (
                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                            {visible.length >= totalCount ? `${totalCount.toLocaleString()} users` : `${visible.length} of ${totalCount.toLocaleString()} users`}
                        </Typography>
                    )}
                    <ExportMenu
                        rows={exportRoster.rows || rows}
                        columns={activeCols}
                        count={totalCount}
                        title="Users"
                        onOpen={() => setWantAll(true)}
                        loading={exportRoster.loading}
                    />
                    <ColumnPicker active={active} setActive={setActive} />
                </Box>

                {/* Table */}
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <Box component="thead">
                            <Box component="tr">
                                <Box component="th" aria-hidden="true" sx={{ borderBottom: '1px solid', borderColor: 'divider', width: '1px' }} />
                                {activeCols.map((c) => {
                                    const isSorted = sortBy === c.id;
                                    return (
                                        <Box
                                            key={c.id}
                                            component="th"
                                            onClick={() => c.sortable && requestSort(c.id)}
                                            sx={{
                                                fontSize: 11, fontWeight: 600, color: isSorted ? 'text.primary' : 'text.disabled',
                                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                                px: 2, py: 1.25, textAlign: c.align || 'left',
                                                borderBottom: '1px solid', borderColor: 'divider',
                                                whiteSpace: 'nowrap',
                                                cursor: c.sortable ? 'pointer' : 'default',
                                                userSelect: 'none',
                                                '&:hover': c.sortable ? { color: 'text.primary' } : undefined,
                                            }}
                                        >
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                                {c.label}
                                                {c.sortable && (
                                                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', ml: 0.25 }}>
                                                        <ArrowDropUpIcon sx={{ fontSize: 12, m: '-3px 0', color: isSorted && sortDir === 'asc' ? 'secondary.main' : 'text.disabled' }} />
                                                        <ArrowDropDownIcon sx={{ fontSize: 12, m: '-3px 0', color: isSorted && sortDir === 'desc' ? 'secondary.main' : 'text.disabled' }} />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {roster.pending && (
                                <Box component="tr">
                                    <Box component="td" colSpan={activeCols.length + 1} sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.disabled', fontSize: 12 }}>Loading users...</Box>
                                </Box>
                            )}
                            {roster.failed && (
                                <Box component="tr">
                                    <Box component="td" colSpan={activeCols.length + 1} sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.secondary', fontSize: 12 }}>
                                        Users could not be loaded. The request may have timed out.
                                        <Button size="small" onClick={roster.retry} sx={{ ml: 1, fontSize: 11, fontWeight: 600, textTransform: 'none' }}>Retry</Button>
                                    </Box>
                                </Box>
                            )}
                            {!serverBlocked && visible.length === 0 && (
                                <Box component="tr">
                                    <Box component="td" colSpan={activeCols.length + 1} sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.disabled', fontSize: 12 }}>No users match current filters</Box>
                                </Box>
                            )}
                            {visible.map((u) => (
                                <Box
                                    component="tr"
                                    key={u.email}
                                    sx={{ borderBottom: '1px solid', borderColor: 'divider', transition: 'background 150ms', '&:hover': { bgcolor: 'action.hover' }, '&:last-child': { borderBottom: 'none' } }}
                                >
                                    <Box component="td" sx={{ px: 2, py: 1.25, width: '1px', whiteSpace: 'nowrap' }}>
                                        <RowViewButton onClick={() => drill('user-detail', u.name, { type: 'user', value: u.email })} />
                                    </Box>
                                    {activeCols.map((c) => {
                                        const raw = c.accessor(u);
                                        const align = c.align || 'left';
                                        if (c.id === 'riskScore') {
                                            return (
                                                <Box component="td" key={c.id} sx={{ px: 2, py: 1.25, textAlign: align }} onClick={(e) => { e.stopPropagation(); if (raw != null) setModal({ email: u.email, name: u.name, dept: u.dept, factors: u._factors }); }}>
                                                    <ScoreCell value={raw} />
                                                </Box>
                                            );
                                        }
                                        if (c.id === 'status') {
                                            return (
                                                <Box component="td" key={c.id} sx={{ px: 2, py: 1.25, textAlign: align }}>
                                                    <StatusCell value={raw} />
                                                </Box>
                                            );
                                        }
                                        const display = typeof c.format === 'function' ? c.format(raw) : raw ?? ', ';
                                        const isName = c.id === 'name';
                                        return (
                                            <Box component="td" key={c.id} sx={{
                                                px: 2, py: 1.25,
                                                textAlign: align,
                                                color: isName ? 'text.primary' : 'text.secondary',
                                                fontWeight: isName ? 600 : 400,
                                                fontSize: c.id === 'email' ? 11 : 12,
                                                whiteSpace: c.id === 'email' ? 'nowrap' : 'normal',
                                            }}>
                                                {display}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Footer */}
                <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', mr: 0.5 }}>Per page</Typography>
                        {PAGE_SIZES.map((n) => {
                            const isActive = pageSize === n;
                            return (
                                <Button key={n} size="small" onClick={() => setPageSize(n)} sx={{
                                    minWidth: 0, fontSize: 11, fontWeight: 600, px: 1.25, py: '3px', borderRadius: '4px',
                                    border: '1px solid', borderColor: isActive ? 'secondary.main' : 'divider',
                                    bgcolor: isActive ? 'secondary.main' : 'transparent',
                                    color: isActive ? 'common.white' : 'text.secondary',
                                    '&:hover': { borderColor: 'secondary.main', bgcolor: isActive ? 'secondary.main' : 'transparent' },
                                }}>{n}</Button>
                            );
                        })}
                    </Box>
                </Box>
            </MainCard>

            <RiskBreakdownModal
                open={!!modal}
                onClose={() => setModal(null)}
                email={modalProps.email}
                name={modalProps.name}
                dept={modalProps.dept}
                serverFactors={modalProps.factors}
                go={go}
            />
        </motion.div>
    );
}
